"use client";

import { FeatureHeroSection } from "@/components/shared/feature_hero_section";
import { AlternatingSection } from "@/components/shared/alternating_section";
import { DynamicEvents } from "@/components/shared/dynamic_events";
import { get__related_how_we_help_pages } from "@/cms/how-we-help-data";
import { useTranslations } from "next-intl";

const ReducedWorkload = () => {
    const t = useTranslations();

    return (
        <main className="flex justify-center" role="main" aria-label="StudyStash Reduced Workload Feature">
            <div className="flex w-full flex-col">
                {/* Hero Section */}
                <FeatureHeroSection
                    icon="/icon-pack/StudyStash - Icon - Home.png"
                    iconAlt="Reduced Workload Icon"
                    category="reduce faculty workload"
                    title="A platform for in class"
                    highlightedText="and self-directed study"
                    description="Reduce faculty workload by letting StudyChat handle thousands of questions - cutting down on 4 a.m. emails and supporting both in-class and self-directed learning."
                    buttonText="Join the pilot"
                    buttonHref="/pilot-program"
                    showDemoModal={true}
                    backgroundImage="/how-we-help/reduced-workload/hero_reduced_faculty_workload.png"
                    backgroundImageAlt="StudyStash Reduced Faculty Workload Dashboard"
                    mobileImage="/how-we-help/reduced-workload/hero_reduced_faculty_workload.png"
                    mobileImageAlt="StudyStash Reduced Faculty Workload Dashboard"
                />

                {/* Alternating Sections */}
                <div className="pt-24 md:pt-40 lg:pt-72">
                    <AlternatingSection 
                        sections={[
                            {
                                subtitle: "Fewer questions, more teaching time",
                                title: "Cut down on 4 a.m. emails and classroom interruptions",
                                content: (
                                    <div>
                                        <p>With StudyChat answering thousands of questions during class and self-directed study, faculty spend less time fielding repetitive queries and more time focusing on meaningful teaching.</p>
                                    </div>
                                ),
                                video: {
                                    src: "/videos/adaptive_learning.mp4",
                                    alt: "Demonstration of StudyChat reducing faculty workload",
                                    containerClassName: "rounded-xl bg-ss-yellow/20 px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8"
                                }
                            },
                            {
                                subtitle: "Spend less time creating assessments",
                                title: "Save hours on formative assessment creation, marking and feedback",
                                content: (
                                    <div>
                                        <p>Automatically generating tailored assessments means lecturers no longer waste time building or marking tests, while students still get timely, {t("general.words.personalised")} feedback that drives learning.</p>
                                    </div>
                                ),
                                video: {
                                    src: "/test_vid.mp4",
                                    alt: "Demonstration of automated test creation and feedback",
                                    containerClassName: "rounded-xl bg-ss-purple/20 px-3 pt-4 shadow-lg transition-all duration-300 hover:shadow-xl md:px-8 md:pt-8"
                                }
                            },
                            {
                                subtitle: "For high-impact lectures",
                                title: "Make the most of your time with students",
                                content: (
                                    <div>
                                        <p>Lecturers receive clear insight reports on student progress, highlighting the topics where the class is struggling-allowing them to focus their limited in-person time on high-impact guidance, reducing workload while ensuring students get targeted support.</p>
                                    </div>
                                ),
                                image: {
                                    src: "/insights_email.png",
                                    alt: "Demonstration of insight reports helping lecturers support students"
                                }
                            }
                            
                        ]}
                    />
                </div>

                {/* Related How We Help Pages */}
                <DynamicEvents 
                    items={get__related_how_we_help_pages("/how-we-help/reduced-workload")} 
                    showViewAll={false}
                    showKeepReading={true} 
                />
            </div>
        </main>
    );
};

export default ReducedWorkload;